"use client"

import { useQuery } from "@tanstack/react-query"
import { Effect } from "effect"
import { RpcClient } from "@effect/rpc"
import { featureAnnouncementsUserEq, FeatureAnnouncementsUserRpc } from "@/lib/effect-query"
import { POLLING_INTERVALS } from "@/shared/config/polling-intervals"
import { useVisibilityAwareInterval } from "@/hooks"

// Deliberately nested under the feature layer's MY_FEEDBACK_QUERY_KEYS.all
// prefix (["my-feedback"]) so broad invalidations there also refresh the badge.
// The feature imports this constant for targeted invalidation on markViewed —
// feature → entity is the legal FSD direction.
export const MY_FEEDBACK_UNREAD_COUNT_QUERY_KEY = ["my-feedback", "unread-count"] as const

/**
 * Aggregate count of the user's unread feedback threads, polled to drive the
 * badge on the feedback entry-point buttons (FAB + navbar). Server-side the
 * count uses the same predicate as the per-row unread dot, so the badge always
 * equals the number of unread rows in the "Your feedback" panel.
 */
export function useMyFeedbackUnreadCount({ enabled = true }: { enabled?: boolean } = {}) {
  const refetchInterval = useVisibilityAwareInterval(POLLING_INTERVALS.REALTIME)

  const { data } = useQuery({
    ...featureAnnouncementsUserEq.queryOptions({
      queryKey: MY_FEEDBACK_UNREAD_COUNT_QUERY_KEY,
      queryFn: () =>
        Effect.gen(function* () {
          const client = yield* RpcClient.make(FeatureAnnouncementsUserRpc)
          return yield* client.getMyFeedbackUnreadCount()
        }).pipe(Effect.scoped),
    }),
    enabled,
    refetchInterval,
    // Polling pauses while the tab is hidden (visibility-aware interval), so a
    // reply that lands while the user is on another tab is only picked up by a
    // focus refetch. "always" bypasses the global 5-minute staleTime — plain
    // `true` would skip the refetch because the cached count is still "fresh".
    refetchOnWindowFocus: "always",
  })

  return data?.count ?? 0
}
